import type { WebVitalName, WebVitalRating } from './types'

type Reporter = (metric: { name: WebVitalName; value: number; rating: WebVitalRating; id: string }) => void

// Thresholds per https://web.dev/vitals/
const THRESHOLDS: Record<WebVitalName, [number, number]> = {
  LCP: [2500, 4000],
  FCP: [1800, 3000],
  CLS: [0.1, 0.25],
  INP: [200, 500],
  TTFB: [800, 1800],
}

function rate(name: WebVitalName, value: number): WebVitalRating {
  const [good, poor] = THRESHOLDS[name]
  if (value <= good) return 'good'
  if (value <= poor) return 'needs-improvement'
  return 'poor'
}

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function safeObserve(type: string, cb: (entries: PerformanceEntry[]) => void): PerformanceObserver | null {
  try {
    const supported = (PerformanceObserver as any).supportedEntryTypes as string[] | undefined
    if (supported && !supported.includes(type)) return null
    const po = new PerformanceObserver((list) => cb(list.getEntries()))
    po.observe({ type, buffered: true } as PerformanceObserverInit)
    return po
  } catch {
    return null
  }
}

export function startWebVitals(report: Reporter): () => void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return () => {}
  }

  const observers: PerformanceObserver[] = []

  // LCP — keep the latest reported value, finalize on hidden/pagehide
  let lcpValue = 0
  let lcpId = uid()
  const lcpObs = safeObserve('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1] as any
    if (last) lcpValue = last.renderTime || last.loadTime || last.startTime || 0
  })
  if (lcpObs) observers.push(lcpObs)

  // FCP — first occurrence only
  let fcpReported = false
  const fcpObs = safeObserve('paint', (entries) => {
    if (fcpReported) return
    for (const e of entries) {
      if (e.name === 'first-contentful-paint') {
        fcpReported = true
        report({ name: 'FCP', value: e.startTime, rating: rate('FCP', e.startTime), id: uid() })
      }
    }
  })
  if (fcpObs) observers.push(fcpObs)

  // CLS — sum of layout shifts excluding user-input ones
  let clsValue = 0
  const clsId = uid()
  const clsObs = safeObserve('layout-shift', (entries) => {
    for (const e of entries as any[]) {
      if (!e.hadRecentInput) clsValue += e.value
    }
  })
  if (clsObs) observers.push(clsObs)

  // INP — track largest interaction duration
  let inpValue = 0
  let inpId = uid()
  const inpObs = safeObserve('event', (entries) => {
    for (const e of entries as any[]) {
      if (e.duration > inpValue) {
        inpValue = e.duration
        inpId = uid()
      }
    }
  })
  if (inpObs) observers.push(inpObs)

  // TTFB — from navigation entry, fire once
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (nav && nav.responseStart > 0) {
      const ttfb = nav.responseStart
      report({ name: 'TTFB', value: ttfb, rating: rate('TTFB', ttfb), id: uid() })
    }
  } catch { /* noop */ }

  const finalize = () => {
    if (lcpValue > 0) {
      report({ name: 'LCP', value: lcpValue, rating: rate('LCP', lcpValue), id: lcpId })
      lcpValue = 0
    }
    if (clsValue > 0) {
      report({ name: 'CLS', value: clsValue, rating: rate('CLS', clsValue), id: clsId })
    }
    if (inpValue > 0) {
      report({ name: 'INP', value: inpValue, rating: rate('INP', inpValue), id: inpId })
      inpValue = 0
    }
  }

  const onHidden = () => {
    if (document.visibilityState === 'hidden') finalize()
  }
  document.addEventListener('visibilitychange', onHidden, { once: false })
  window.addEventListener('pagehide', finalize, { once: true })

  return () => {
    observers.forEach((o) => { try { o.disconnect() } catch { /* noop */ } })
    document.removeEventListener('visibilitychange', onHidden)
    window.removeEventListener('pagehide', finalize)
  }
}
